import React from "react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"

import type { ChartClickData } from "../../types/chart"
import type {
  BucketClickData,
  DashboardWidget,
  DashboardWidgetConfig,
} from "../../types/core"
import { WidgetFillerContent } from "../WidgetFillerContent"
import { GridWidgetContainer } from "./GridWidgetContainer"

interface GridDashboardWidgetProps {
  widget: DashboardWidget
  dashboardId: string
  onRemove: (id: string) => void
  onConfigure: (id: string) => void
  onClone?: (id: string) => void
  onAlert?: (id: string) => void
  onConfigChange: (id: string, config: DashboardWidgetConfig) => void
  onWidthChange?: (id: string, columns: number) => void
  onPointClick?: (
    point: BucketClickData,
    widgetId: string,
    metricId?: string,
  ) => void
  onChartClick?: (
    point: BucketClickData,
    widgetId: string,
    metricId?: string,
  ) => void
  onCellClick?: (
    clickData: ChartClickData,
    conversationsHref: string | null,
    baselineId?: string,
  ) => void
}

export const GridDashboardWidget: React.FC<GridDashboardWidgetProps> = ({
  widget,
  dashboardId,
  onRemove,
  onConfigure,
  onClone,
  onAlert,
  onConfigChange,
  onWidthChange,
  onChartClick,
  onCellClick,
  onPointClick,
}) => {
  const { trackEvent } = useAnalytics()

  // Handle point click events for focus mode integration
  const handlePointClick = (
    point: BucketClickData,
    _widgetId: string,
    metricId?: string,
  ) => {
    if (onPointClick) {
      onPointClick(point, widget.id, metricId)

      // Track widget clicked
      trackEvent(TRACKING_EVENTS.DASHBOARD.WIDGET_CLICKED, {
        widget_id: widget.id,
        widget_type: widget.type,
        metric_id: metricId,
        click_target: "data_point",
      })
    }
  }

  const handleChartClick = (
    point: BucketClickData,
    _widgetId: string,
    metricId?: string,
  ) => {
    if (onChartClick) {
      onChartClick(point, widget.id, metricId)

      trackEvent(TRACKING_EVENTS.DASHBOARD.WIDGET_CLICKED, {
        widget_id: widget.id,
        widget_type: widget.type,
        metric_id: metricId,
        click_target: "chart",
      })
    }
  }

  const handleCellClick = (
    clickData: ChartClickData,
    conversationsHref: string | null,
  ) => {
    onCellClick?.(clickData, conversationsHref, widget.config?.baselineId)
    trackEvent(TRACKING_EVENTS.DASHBOARD.WIDGET_CLICKED, {
      widget_id: widget.id,
      widget_type: widget.type,
      metric_id: clickData.metricId,
      click_target: "data_point",
    })
  }

  return (
    <GridWidgetContainer
      widget={widget}
      dashboardId={dashboardId}
      onRemove={onRemove}
      onConfigure={onConfigure}
      onClone={onClone}
      onAlert={onAlert}
      onConfigChange={onConfigChange}
      onWidthChange={onWidthChange}
    >
      <WidgetFillerContent
        type={widget.type}
        widget={widget}
        onChartClick={handleChartClick}
        onPointClick={handlePointClick}
        onCellClick={handleCellClick}
      />
    </GridWidgetContainer>
  )
}
